import { useState, useEffect } from 'react';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Fade, TextField, InputAdornment, Chip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import Pagination from '../../common/Pagination';
import LogoLoader from '../../common/LogoLoader';
import * as XLSX from 'xlsx-js-style';

const STATUS_OPTIONS = [
  { value: 0, label: 'SELECT' },
  { value: 1, label: 'New' },
  { value: 2, label: 'InProgress' },
  { value: 3, label: 'Pending' },
  { value: 4, label: 'Completed' },
  { value: 5, label: 'Delivered' },
];

const STATUS_COLOR = {
  New: '#1565c0',
  InProgress: '#f57c00',
  Pending: '#6a1b9a',
  Completed: '#388e3c',
  Delivered: '#7b1fa2',
};

function WorkOrderItemsReport() {
  useSessionValidation();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { isAuthenticated, companyId, logout, token, companyName } = useAuth();

  const today = new Date().toISOString().split('T')[0];
  const now = new Date();
  const firstOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;

  const [fromDate, setFromDate] = useState(firstOfMonth);
  const [toDate, setToDate] = useState(today);
  const [statusId, setStatusId] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    setMounted(true);
    if (isAuthenticated && tokenIsExpired(token)) logout();
  }, [isAuthenticated, companyId]); // eslint-disable-line

  const fetchTableData = async () => {
    if (!companyId || !token) return;
    setLoading(true);
    try {
      const response = await fetchData(
        'ReportApi',
        `/WorkOrderReport/${companyId}/${statusId || 0}/${fromDate}/${toDate}`,
        token
      );
      if (response && response.responseCode === '1111' && response.response_data) {
        const rows = Array.isArray(response.response_data)
          ? response.response_data
          : [response.response_data];
        setData(rows);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching WorkOrder Items report:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => { setCurrentPage(1); fetchTableData(); };

  const filteredData = data.filter(item =>
    item.ClientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(item.OrderNumber || '').includes(searchTerm) ||
    item.Glassname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.Framename?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.Statusname?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const headerCell = (align = 'left') => ({
    color: 'white', fontWeight: '600', fontSize: '11px',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    padding: '4px 10px', textAlign: align,
    whiteSpace: 'nowrap', backgroundColor: '#5c3d1e',
  });

  const bodyCell = (align = 'left', bold = false) => ({
    fontSize: '12px', padding: '6px 10px',
    color: '#2d3748', whiteSpace: 'nowrap',
    textAlign: align, fontWeight: bold ? '600' : '400',
  });

  const COLS = 15;

  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();
    const nowTs = new Date();
    const generatedTime = nowTs.toLocaleString('en-IN', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
    });
    const statusLabel = STATUS_OPTIONS.find(s => s.value === statusId)?.label || 'All';

    const aoa = [
      [companyName || 'Work Order Items Report', ...Array(COLS - 1).fill('')],
      ['Work Order Items Report', ...Array(COLS - 1).fill('')],
      [`Date Range : ${fromDate}  To : ${toDate}`, ...Array(COLS - 1).fill('')],
      [`Status Filter : ${statusLabel}`, ...Array(COLS - 1).fill('')],
      [`Generated : ${generatedTime}`, ...Array(COLS - 1).fill('')],
      Array(COLS).fill(''),
      ['Order Number', 'Order Date', 'Estimate Date', 'Client Name',
       'Glass', 'Frame', 'Thickness', 'Board', 'Lamination',
       'Mount', 'Hook', 'Stand', 'Print Type', 'Status', 'Remarks'],
      ...filteredData.map(item => [
        item.OrderNumber || '',
        item.OrderDate ? item.OrderDate.split('T')[0] : '',
        item.EstimateDate ? item.EstimateDate.split('T')[0] : '',
        item.ClientName || '',
        item.Glassname || '',
        item.Framename || '',
        item.Thickness || '',
        item.Boardname || '',
        item.Lamination || '',
        item.Mountname || '',
        item.Hookname || '',
        item.Standname || '',
        item.Printtype || '',
        item.Statusname || '',
      ]),
    ];

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    ws['!cols'] = [
      { wch: 13 }, { wch: 12 }, { wch: 14 }, { wch: 22 },
      { wch: 14 }, { wch: 16 }, { wch: 10 }, { wch: 14 },
      { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
      { wch: 12 }, { wch: 12 }, { wch: 20 },
    ];

    ws['!rows'] = [
      { hpt: 22 }, { hpt: 18 }, { hpt: 16 }, { hpt: 16 }, { hpt: 16 }, { hpt: 6 }, { hpt: 20 },
    ];

    ws['!merges'] = [0, 1, 2, 3, 4].map(r => ({ s: { r, c: 0 }, e: { r, c: COLS - 1 } }));

    const infoS = (sz, bold, color = '5c3d1e') => ({
      font: { bold, sz, color: { rgb: color } },
      alignment: { horizontal: 'center', vertical: 'center' },
      fill: { patternType: 'solid', fgColor: { rgb: 'FFF8EE' } },
    });

    ws['A1'].s = infoS(16, true);
    ws['A2'].s = infoS(13, true, '2d3748');
    ws['A3'].s = infoS(11, false, '444444');
    ws['A4'].s = infoS(11, false, '444444');
    ws['A5'].s = infoS(11, false, '444444');

    const hdrS = {
      font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } },
      fill: { patternType: 'solid', fgColor: { rgb: '5c3d1e' } },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: {
        top: { style: 'medium', color: { rgb: '3b2410' } },
        bottom: { style: 'medium', color: { rgb: '3b2410' } },
        left: { style: 'thin', color: { rgb: '3b2410' } },
        right: { style: 'thin', color: { rgb: '3b2410' } },
      },
    };

    for (let c = 0; c < COLS; c++) {
      const addr = XLSX.utils.encode_cell({ r: 6, c });
      if (ws[addr]) ws[addr].s = hdrS;
    }

    for (let r = 7; r < aoa.length; r++) {
      const even = (r - 7) % 2 === 0;
      for (let c = 0; c < COLS; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        if (!ws[addr]) ws[addr] = { t: 's', v: '' };
        ws[addr].s = {
          font: { sz: 10 },
          fill: { patternType: 'solid', fgColor: { rgb: even ? 'FFFFFF' : 'FDF6EC' } },
          alignment: { horizontal: 'left', vertical: 'center' },
          border: {
            top: { style: 'thin', color: { rgb: 'E0E0E0' } },
            bottom: { style: 'thin', color: { rgb: 'E0E0E0' } },
            left: { style: 'thin', color: { rgb: 'E0E0E0' } },
            right: { style: 'thin', color: { rgb: 'E0E0E0' } },
          },
        };
      }
    }

    XLSX.utils.book_append_sheet(wb, ws, 'Work Order Items');
    XLSX.writeFile(wb, `WorkOrderItemsReport_${fromDate}_${toDate}.xlsx`, { bookSST: false, cellStyles: true });
  };

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{ minWidth: '100%', background: '#fdf6ec', padding: '10px', position: 'relative' }}>
        {loading && <LogoLoader open={loading} message="Loading Work Order Items..." />}

        <div style={{
          margin: '0 auto', background: 'white', borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px',
          color: 'black', fontFamily: 'Times New Roman', overflow: 'hidden',
        }}>
          {/* Title */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
            <h4 style={{ fontSize: '17px', fontWeight: '700', margin: 0, letterSpacing: '-0.3px', fontFamily: 'Times New Roman', textTransform: 'uppercase' }}>
              <span style={{ color: 'black' }}>🗂️ Work Order Items</span>
            </h4>
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', whiteSpace: 'nowrap' }}>From Date</label>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
              style={{ border: '1px solid #c9a84c', borderRadius: '6px', padding: '3px 8px', fontSize: '12px', height: '29px', background: '#fdf6ec', color: '#333', outline: 'none', cursor: 'pointer' }} />
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', whiteSpace: 'nowrap' }}>To date</label>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
              style={{ border: '1px solid #c9a84c', borderRadius: '6px', padding: '3px 8px', fontSize: '12px', height: '29px', background: '#fdf6ec', color: '#333', outline: 'none', cursor: 'pointer' }} />
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', whiteSpace: 'nowrap' }}>Status</label>
            <select value={statusId} onChange={e => setStatusId(Number(e.target.value))}
              style={{ border: '1px solid #c9a84c', borderRadius: '6px', padding: '3px 8px', fontSize: '12px', height: '29px', background: '#fdf6ec', color: '#333', outline: 'none', cursor: 'pointer', minWidth: '130px' }}>
              {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <Button variant="contained" onClick={handleRefresh}
              style={{ background: 'linear-gradient(135deg, #388e3c 0%, #43a047 100%)', color: 'white', padding: '4px 16px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', border: 'none', minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(56,142,60,0.4)' }}>
              Refresh
            </Button>
            <Button variant="contained" onClick={handleExportExcel} disabled={filteredData.length === 0}
              style={{ background: filteredData.length === 0 ? '#ccc' : 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)', color: 'white', padding: '4px 16px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', border: 'none', minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap', boxShadow: filteredData.length === 0 ? 'none' : '0 2px 8px rgba(21,101,192,0.4)' }}>
              📥 Excel
            </Button>
            <div style={{ flex: 1 }} />
            <TextField size="small" placeholder="Search..." value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ color: '#c9a84c', fontSize: '18px' }} /></InputAdornment> }}
              sx={{ minWidth: '180px', '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#fdf6ec', fontSize: '13px', height: '29px', '&:hover fieldset': { borderColor: '#c9a84c' }, '&.Mui-focused fieldset': { borderColor: '#c9a84c' } } }} />
          </div>

          {/* Table */}
          <div style={{ marginBottom: '10px' }}>
            <style>{`
              .woi-scroll::-webkit-scrollbar { height: 10px; width: 8px; }
              .woi-scroll::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
              .woi-scroll::-webkit-scrollbar-thumb { background: #c9a84c; border-radius: 4px; }
              .woi-scroll::-webkit-scrollbar-thumb:hover { background: #5c3d1e; }
              .woi-scroll { scrollbar-width: thin; scrollbar-color: #c9a84c #f1f1f1; }
            `}</style>
            <div className="woi-scroll" style={{ overflowX: 'scroll', overflowY: 'auto', maxHeight: 'calc(100vh - 260px)', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <TableContainer component={Paper} sx={{ boxShadow: 'none', borderRadius: 0, overflow: 'visible' }}>
                <Table stickyHeader sx={{ minWidth: 1400 }} aria-label="work order items table">
                  <TableHead>
                    <TableRow style={{ height: '27px' }}>
                      <TableCell style={headerCell()}>Order No</TableCell>
                      <TableCell style={headerCell()}>Order Date</TableCell>
                      <TableCell style={headerCell()}>Estimate Date</TableCell>
                      <TableCell style={headerCell()}>Client Name</TableCell>
                      <TableCell style={headerCell()}>Glass</TableCell>
                      <TableCell style={headerCell()}>Frame</TableCell>
                      <TableCell style={headerCell()}>Thickness</TableCell>
                      <TableCell style={headerCell()}>Board</TableCell>
                      <TableCell style={headerCell()}>Lamination</TableCell>
                      <TableCell style={headerCell()}>Mount</TableCell>
                      <TableCell style={headerCell()}>Hook</TableCell>
                      <TableCell style={headerCell()}>Stand</TableCell>
                      <TableCell style={headerCell()}>Print Type</TableCell>
                      <TableCell style={headerCell('center')}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={14} style={{ textAlign: 'center', padding: '24px', color: '#999', fontSize: '13px' }}>
                          {loading ? 'Loading...' : 'No records found. Select filters and click Refresh.'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {paginatedData.map((item, index) => (
                          <TableRow key={item.EstimateId || index}
                            style={{ background: index % 2 === 0 ? '#ffffff' : '#fdf6ec', borderBottom: '1px solid #f0f0f0' }}>
                            <TableCell style={bodyCell('left', true)}>{item.OrderNumber}</TableCell>
                            <TableCell style={bodyCell()}>{item.OrderDate ? item.OrderDate.split('T')[0] : '—'}</TableCell>
                            <TableCell style={bodyCell()}>{item.EstimateDate ? item.EstimateDate.split('T')[0] : '—'}</TableCell>
                            <TableCell style={bodyCell('left', true)}>{item.ClientName || '—'}</TableCell>
                            <TableCell style={bodyCell()}>{item.Glassname || '—'}</TableCell>
                            <TableCell style={bodyCell()}>{item.Framename || '—'}</TableCell>
                            <TableCell style={bodyCell()}>{item.Thickness || '—'}</TableCell>
                            <TableCell style={bodyCell()}>{item.Boardname || '—'}</TableCell>
                            <TableCell style={bodyCell()}>{item.Lamination || '—'}</TableCell>
                            <TableCell style={bodyCell()}>{item.Mountname || '—'}</TableCell>
                            <TableCell style={bodyCell()}>{item.Hookname || '—'}</TableCell>
                            <TableCell style={bodyCell()}>{item.Standname || '—'}</TableCell>
                            <TableCell style={bodyCell()}>{item.Printtype || '—'}</TableCell>
                            <TableCell style={bodyCell('center')}>
                              {item.Statusname ? (
                                <Chip label={item.Statusname} size="small"
                                  style={{ fontSize: '10px', height: '18px', backgroundColor: STATUS_COLOR[item.Statusname] || '#757575', color: 'white', fontWeight: '600' }} />
                              ) : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Total Row */}
                        <TableRow style={{ background: '#5c3d1e', borderTop: '2px solid #3b2410' }}>
                          <TableCell colSpan={4} style={{ ...bodyCell('left', true), color: 'white', background: '#5c3d1e', fontSize: '12px' }}>
                            Total: {filteredData.length} Records
                          </TableCell>
                          <TableCell style={{ ...bodyCell(), color: 'white', background: '#5c3d1e' }} />
                          <TableCell style={{ ...bodyCell(), color: 'white', background: '#5c3d1e' }} />
                          <TableCell style={{ ...bodyCell('left', true), color: 'white', background: '#5c3d1e' }}>
                            {filteredData.reduce((sum, r) => sum + (parseFloat(r.Thickness) || 0), 0).toFixed(2)}
                          </TableCell>
                          <TableCell style={{ ...bodyCell(), color: 'white', background: '#5c3d1e' }} />
                          <TableCell style={{ ...bodyCell(), color: 'white', background: '#5c3d1e' }} />
                          <TableCell style={{ ...bodyCell(), color: 'white', background: '#5c3d1e' }} />
                          <TableCell style={{ ...bodyCell(), color: 'white', background: '#5c3d1e' }} />
                          <TableCell style={{ ...bodyCell('left', true), color: 'white', background: '#5c3d1e' }}>
                            {filteredData.reduce((sum, r) => sum + (parseFloat(r.Standname) || 0), 0)}
                          </TableCell>
                          <TableCell style={{ ...bodyCell(), color: 'white', background: '#5c3d1e' }} />
                          <TableCell style={{ ...bodyCell(), color: 'white', background: '#5c3d1e' }} />
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </div>

          <Pagination
            currentPage={currentPage} totalPages={totalPages}
            totalItems={totalItems} itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={n => { setItemsPerPage(n); setCurrentPage(1); }}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>
      </div>
    </Fade>
  );
}

export default WorkOrderItemsReport;
